import { Box } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import Collaboration from '@tiptap/extension-collaboration';
import { Document } from '@tiptap/extension-document';
import { Placeholder } from '@tiptap/extension-placeholder';
import { AnyExtension, Editor, JSONContent, useEditor } from '@tiptap/react';
import clsx from 'clsx';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import xss from 'xss';
import type { XmlFragment as YXmlFragment } from 'yjs';

import { useContentLanguage } from 'shared/components/providers/ContentLanguageProvider';
import { RichTextExtraTools, RichTextTools, ToolbarType } from 'shared/types/RichTextToolbar';

import { guardNodeViewRedraws, resolveNodeViews } from './nodeViews';
import { holdRemovedPointerTarget, installStaticInteractions, registerStaticCopySource } from './staticInteractions';
import { STATIC_RICH_TEXT_ATTRIBUTE, renderRichTextStatically } from './staticRender';
import { useRichTextStyles } from './useRichTextStyles';
import { getDefaultExtensions } from '../../../utils/tiptap/convert';
import { PreserveBlockTypeOnSelectAll } from '../../../utils/tiptap/extensions/PreserveBlockTypeOnSelectAll';
import { TooltipNodeContext } from '../../../utils/tiptap/extensions/Tooltip/components/TooltipNode';
import { CustomTextAlign } from '../../../utils/tiptap/extensions/customTextAlign';
import { ReadOnlyAria } from '../../../utils/tiptap/extensions/readOnlyAria';
import { BlockTitleWidgetStyles, TextWidgetStyles, WidgetDefaultStyles } from '../../../utils/typographyStyles';
import { RichTextEditorFixed } from '../RichTextEditorFixed';
import { RichTextEditorInline } from '../RichTextEditorInline';

const defaultExtraExtensions: AnyExtension[] = [];

// A static doc's hover-live editor and the inputs it was built from.
type LiveEditorState = {
  buildExtensions: () => AnyExtension[];
  contentKey: string;
  editor: Editor;
  preserveWhitespace?: true | 'full';
};

// The post-mount createNodeViews of read-only node-view docs is queued and run for all editors together
// once the React commit has finished (one microtask). Every editor's DOM is then in place before the
// first node-view redraw reads the DOM selection, so those reads stop forcing one layout per editor.
const pendingNodeViews: Array<() => void> = [];
function queueNodeViews(run: () => void) {
  pendingNodeViews.push(run);
  if (pendingNodeViews.length > 1) return;
  queueMicrotask(() => pendingNodeViews.splice(0).forEach(next => next()));
}

const HeadingDocument = Document.extend({
  content: 'heading+'
});

export type RichTextProps = {
  dataTestIdPrefix?: string;
  fragment?: YXmlFragment;
  placeholder?: string;
  htmlContent?: string;
  contentEditable?: boolean;
  /**
   * ProseMirror JSON
   */
  pmContent?: JSONContent;
  editable?: boolean;
  extraExtensions?: AnyExtension[];
  toolbarType?: ToolbarType;
  tools?: (RichTextTools | RichTextExtraTools)[];
  customControls?: ReactNode;
  className?: string;
  toolbarClassName?: string;
  withBorder?: boolean;
  expandHeight?: true;
  preserveWhitespace?: true | 'full';
  widgetStyles?: TextWidgetStyles | BlockTitleWidgetStyles;
  widgetDefaultStyles?: WidgetDefaultStyles;
  onChange?: (newValue: string) => void;
  setEditor?: (editor: Editor) => void;
  shouldHideTooltip?: boolean;
  tooltipClassName?: string;
  defaultTextAlignment?: 'left' | 'right' | 'center';
  tooltipWithinPortal?: boolean;
};

/**
 * How a rich text renders. Read-only ProseMirror JSON without a toolbar is rendered `static`: the editor's
 * exact DOM with no editor behind it (see staticRender.ts), which is what keeps a page with dozens of
 * question texts and table cells from mounting dozens of editors at once. Anything a user can edit, and
 * collaborative or HTML content, renders `live`.
 */
export function resolveRichTextRenderMode(props: RichTextProps): 'static' | 'live' {
  const { contentEditable = true, editable = true, fragment, pmContent, toolbarType = ToolbarType.None } = props;
  const isReadOnly = !(editable && contentEditable);
  if (isStaticRenderingOff()) return 'live';
  return isReadOnly && !fragment && pmContent && toolbarType === ToolbarType.None ? 'static' : 'live';
}

export const STATIC_RENDERING_OFF_STORAGE_KEY = 'hendrx.richText.staticRendering';

/**
 * Turns static rendering off in one browser: every rich text there renders live, as it did before static rendering
 * existed. `localStorage['hendrx.richText.staticRendering'] = 'off'` compares a user's machine with and without it.
 */
function isStaticRenderingOff() {
  try {
    return window.localStorage.getItem(STATIC_RENDERING_OFF_STORAGE_KEY) === 'off';
  } catch {
    return false;
  }
}

export function RichText(props: RichTextProps) {
  return resolveRichTextRenderMode(props) === 'static' ? <StaticRichText {...props} /> : <LiveRichText {...props} />;
}

function buildExtensions({
  defaultAlignment,
  extraExtensions,
  fragment,
  placeholder
}: {
  defaultAlignment: 'left' | 'right' | 'center';
  extraExtensions: AnyExtension[];
  fragment?: YXmlFragment;
  placeholder: string;
}): AnyExtension[] {
  return [
    ...getDefaultExtensions({ useNativeHistory: !fragment }),
    ...extraExtensions,
    CustomTextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment }),
    Placeholder.configure({
      placeholder,
      showOnlyWhenEditable: false
    }),
    PreserveBlockTypeOnSelectAll,
    ReadOnlyAria,
    ...(fragment
      ? [
          Collaboration.configure({
            fragment
          })
        ]
      : [])
  ];
}

function StaticRichText(props: RichTextProps) {
  const {
    className,
    dataTestIdPrefix = '',
    defaultTextAlignment,
    expandHeight,
    extraExtensions = defaultExtraExtensions,
    placeholder = '',
    pmContent,
    preserveWhitespace,
    shouldHideTooltip,
    tools = Object.values(RichTextTools),
    tooltipClassName,
    tooltipWithinPortal,
    widgetDefaultStyles,
    widgetStyles,
    withBorder = false
  } = props;
  const { contentDirection } = useContentLanguage();
  const defaultAlignment = defaultTextAlignment ?? (contentDirection === 'ltr' ? 'left' : 'right');
  const { classes, style: richTextStyle } = useRichTextStyles({ expandHeight, widgetStyles, widgetDefaultStyles });
  const textLevelOnly = tools.includes(RichTextExtraTools.TextLevelOnly);
  const isEmpty = !pmContent?.content || pmContent.content.length === 0;

  // Keyed by the content's value: an equal doc must reuse the same HTML and node-view elements, or the
  // HTML would be replaced under the node views already mounted in it.
  const contentKey = useMemo(() => JSON.stringify(pmContent ?? null), [pmContent]);
  const buildDocExtensions = useCallback(() => {
    const base = buildExtensions({ defaultAlignment, extraExtensions, placeholder });
    return textLevelOnly ? [...base, HeadingDocument] : base;
  }, [defaultAlignment, extraExtensions, placeholder, textLevelOnly]);
  const rendered = useMemo(
    () =>
      renderRichTextStatically({
        buildExtensions: buildDocExtensions,
        configKey: `${defaultAlignment}|${placeholder}|${textLevelOnly}`,
        doc: isEmpty ? undefined : pmContent,
        extraExtensions,
        preserveWhitespace
      }),
    // pmContent is covered by contentKey.
    [
      buildDocExtensions,
      contentKey,
      defaultAlignment,
      extraExtensions,
      isEmpty,
      placeholder,
      preserveWhitespace,
      textLevelOnly
    ]
  );
  // React 19 re-applies dangerouslySetInnerHTML whenever the object changes; keep one per rendered doc.
  const innerHtml = useMemo(() => (rendered ? { __html: rendered.html } : undefined), [rendered]);

  // The node views mount into the `.react-renderer` elements of the static HTML, before the first paint.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [nodeViewTargets, setNodeViewTargets] = useState<Element[]>([]);
  useLayoutEffect(() => {
    if (!rendered || rendered.nodeViews.length === 0 || !contentRef.current) return;
    setNodeViewTargets(Array.from(contentRef.current.querySelectorAll('.react-renderer')));
  }, [rendered]);

  // Text selection in a read-only editor is ProseMirror's, not the browser's: a triple-click selects exactly
  // the clicked block's text, and drag-select and copy go through the view. To keep that behaviour, a doc
  // without node views becomes a live editor as soon as a mouse or pen pointer enters it — before any click
  // reaches it. The live DOM is the static DOM (same renderer, same extensions), so the swap is invisible.
  // Touch is left static: a pointer that only arrives with the press would swap the DOM under the finger.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [live, setLive] = useState<LiveEditorState | null>(null);
  const releaseHeldNode = useRef<(() => void) | null>(null);
  // A live editor holds the content and extensions it was created with. When either changes it is stale: the
  // doc returns to static, which shows the new content in this same render, and turns live again on the
  // next hover.
  const liveIsCurrent =
    live?.contentKey === contentKey &&
    live.buildExtensions === buildDocExtensions &&
    live.preserveWhitespace === preserveWhitespace;
  if (live && !liveIsCurrent) setLive(null);
  const liveEditor = liveIsCurrent ? live.editor : null;
  const canGoLive = Boolean(rendered && rendered.nodeViews.length === 0);
  useEffect(() => {
    const root = rootRef.current;
    if (!canGoLive || liveEditor || !root) return undefined;
    const goLive = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      root.removeEventListener('pointerover', goLive);
      const editor = new Editor({
        element: document.createElement('div'),
        extensions: buildDocExtensions(),
        parseOptions: { preserveWhitespace },
        editable: false,
        content: isEmpty ? undefined : pmContent,
        // No node views in this doc: skip EditorContent's post-mount node-view redraw.
        onBeforeCreate: ({ editor: created }) => guardNodeViewRedraws(created)
      });
      // Commit now, so a click that follows at once already lands on the live view.
      flushSync(() => setLive({ editor, contentKey, buildExtensions: buildDocExtensions, preserveWhitespace }));
      releaseHeldNode.current?.();
      releaseHeldNode.current = holdRemovedPointerTarget(root, event.target);
    };
    root.addEventListener('pointerover', goLive);
    return () => root.removeEventListener('pointerover', goLive);
  }, [buildDocExtensions, canGoLive, contentKey, isEmpty, liveEditor, pmContent, preserveWhitespace]);
  useEffect(() => () => releaseHeldNode.current?.(), []);
  // Destroys the editor once it is replaced, reset to static, or unmounted.
  useEffect(() => () => live?.editor.destroy(), [live]);

  // Triple-click, the formula selection ring and copy on the static doc behave as the live editor's (see
  // staticInteractions.ts).
  useLayoutEffect(() => {
    const root = contentRef.current?.querySelector('.ProseMirror');
    if (!rendered || !root || liveEditor) return;
    installStaticInteractions();
    registerStaticCopySource(root, rendered.copySource);
  }, [liveEditor, rendered]);

  if (!rendered) return <LiveRichText {...props} />;

  return (
    <TooltipNodeContext.Provider
      value={{
        shouldHide: shouldHideTooltip,
        isEditable: false,
        className: tooltipClassName,
        withinPortal: tooltipWithinPortal
      }}
    >
      <Box
        ref={rootRef}
        style={richTextStyle}
        className={clsx(classes.wrapper, className, withBorder ? classes.richTextInline : '')}
      >
        <RichTextEditor
          data-testid={`${dataTestIdPrefix}-rich-text-editor`}
          classNames={{
            control: classes.control,
            toolbar: classes.toolbar,
            content: clsx(classes.content, { [classes.contentExpandHeight]: expandHeight }),
            root: classes.root,
            typographyStylesProvider: classes.typographyStylesProvider
          }}
          editor={liveEditor}
        >
          {liveEditor ? (
            <RichTextEditor.Content />
          ) : (
            <RichTextEditor.Content
              ref={contentRef}
              dangerouslySetInnerHTML={innerHtml}
              {...{ [STATIC_RICH_TEXT_ATTRIBUTE]: '' }}
            />
          )}
        </RichTextEditor>
      </Box>
      {nodeViewTargets.map((target, index) =>
        rendered.nodeViews[index] ? createPortal(rendered.nodeViews[index], target, String(index)) : null
      )}
    </TooltipNodeContext.Provider>
  );
}

function LiveRichText(props: RichTextProps) {
  const {
    className,
    contentEditable = true,
    dataTestIdPrefix = '',
    defaultTextAlignment,
    editable = true,
    expandHeight,
    extraExtensions = defaultExtraExtensions,
    fragment,
    htmlContent,
    onChange,
    placeholder = '',
    pmContent,
    preserveWhitespace,
    setEditor,
    shouldHideTooltip,
    toolbarType = ToolbarType.None,
    tools = Object.values(RichTextTools),
    tooltipClassName,
    tooltipWithinPortal,
    widgetDefaultStyles,
    widgetStyles,
    withBorder = false
  } = props;

  const isCollaborative = Boolean(fragment);
  const isHtmlContent = Boolean(htmlContent && !fragment && !pmContent);
  // The static serializer strips empty content arrays for roundtrip symmetry, so an
  // empty Y.XmlFragment arrives here as `{ type: 'doc' }`. Passing that to Tiptap yields
  // a doc with zero children (no paragraph, no line-height). Falling back to `undefined`
  // lets the schema's createAndFill seed a default block — matching the YJS path where
  // the Collaboration extension always provides at least one empty paragraph.
  const hasEmptyPmContent = Boolean(pmContent && (!pmContent.content || pmContent.content.length === 0));

  const { contentDirection } = useContentLanguage();
  const defaultAlignment = defaultTextAlignment ?? (contentDirection === 'ltr' ? 'left' : 'right');
  const { classes, style: richTextStyle } = useRichTextStyles({
    expandHeight,
    widgetStyles,
    widgetDefaultStyles
  });

  const textLevelOnly = tools.includes(RichTextExtraTools.TextLevelOnly);
  const extensions = useMemo(() => {
    const base = buildExtensions({ defaultAlignment, extraExtensions, fragment, placeholder });
    return textLevelOnly ? [...base, HeadingDocument] : base;
  }, [defaultAlignment, extraExtensions, placeholder, fragment, textLevelOnly]);

  const needsNodeViews = useMemo(
    () => isCollaborative || isHtmlContent || !pmContent || resolveNodeViews(pmContent, extensions).mode !== 'none',
    [extensions, isCollaborative, isHtmlContent, pmContent]
  );

  const editor = useEditor(
    {
      // EditorContent calls createNodeViews again on mount so React node views can portal into it. That
      // call redraws the whole doc and, in Chrome, reads the DOM selection — a forced full-page layout per
      // editor, inside one React commit, which froze pages with dozens of rich-text cells. A doc with no
      // node-view nodes renders the same without it, so a read-only one keeps only the call made at
      // construction; a read-only one WITH node views still needs it, but runs it batched after the
      // commit (queueNodeViews). Editable editors are left alone: they could gain such a node later.
      onBeforeCreate: ({ editor: created }) => {
        if (editable && contentEditable) return;
        guardNodeViewRedraws(
          created,
          needsNodeViews
            ? redraw =>
                queueNodeViews(() => {
                  if (created.isDestroyed) return;
                  redraw();
                  // The mount nudge below may already have run; flush the node views queued just now.
                  created.view.dispatch(created.state.tr);
                })
            : undefined
        );
      },
      parseOptions: {
        preserveWhitespace
      },
      editable: editable ? contentEditable : false,
      extensions,
      onUpdate: ({ editor }) => {
        onChange?.(editor.getHTML());
      },
      content: isCollaborative
        ? undefined // Collaboration controls content
        : hasEmptyPmContent
        ? undefined // Let Tiptap seed the default block so empty content still has height
        : pmContent
        ? pmContent // ProseMirror JSON
        : htmlContent, // HTML
      immediatelyRender: false,
      shouldRerenderOnTransaction: true
    },
    [extensions, htmlContent, contentEditable, isCollaborative, pmContent, hasEmptyPmContent]
  );

  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  useEffect(() => {
    if (editable && setEditor && editor) {
      setEditor(editor);
    }
  }, [editable, editor, setEditor]);

  useEffect(() => {
    // ReactNodeViewRenderer queues React mounts for inline node views, but in non-collaborative
    // (Player) mode no transaction ever fires after editor creation — there is no YJS sync and
    // the editor is read-only — so the queued mounts never commit until the user interacts.
    // Studio uses YJS, whose Collaboration extension dispatches transactions on initial sync,
    // so it does not need this nudge.
    // A doc without node views has no queued mounts to flush.
    if (!editor || isCollaborative || !needsNodeViews) return;
    editor.view.dispatch(editor.state.tr);
  }, [editor, isCollaborative, needsNodeViews]);

  const { t } = useTranslation('widgets.richTextToolbar');

  const labels = useMemo(
    () => ({
      alignCenterControlLabel: t('alignCenter'),
      alignLeftControlLabel: t('alignLeft'),
      alignRightControlLabel: t('alignRight'),
      blockquoteControlLabel: t('blockquote'),
      boldControlLabel: t('bold'),
      clearFormattingControlLabel: t('clearFormatting'),
      codeControlLabel: t('code'),
      hrControlLabel: t('hr'),
      italicControlLabel: t('italic'),
      linkControlLabel: t('link'),
      strikeControlLabel: t('strikethrough'),
      underlineControlLabel: t('underline'),
      unlinkControlLabel: t('unlink')
    }),
    [t]
  );

  return (
    <TooltipNodeContext.Provider
      value={{
        shouldHide: shouldHideTooltip,
        isEditable: editable,
        className: tooltipClassName,
        withinPortal: tooltipWithinPortal
      }}
    >
      <Box style={richTextStyle} className={clsx(classes.wrapper, className, withBorder ? classes.richTextInline : '')}>
        <RichTextEditor
          data-testid={`${dataTestIdPrefix}-rich-text-editor`}
          classNames={{
            control: classes.control,
            toolbar: classes.toolbar,
            content: clsx(classes.content, { [classes.contentExpandHeight]: expandHeight }),
            root: classes.root,
            typographyStylesProvider: classes.typographyStylesProvider
          }}
          editor={editor}
          labels={labels}
        >
          {toolbarType === ToolbarType.Fixed ? (
            <RichTextEditorFixed {...props} />
          ) : toolbarType === ToolbarType.Inline ? (
            <RichTextEditorInline {...props} />
          ) : (
            <RichTextEditor.Content
              dangerouslySetInnerHTML={
                isHtmlContent && htmlContent && isInitialRender ? { __html: xss(htmlContent) } : undefined
              }
            />
          )}
        </RichTextEditor>
      </Box>
    </TooltipNodeContext.Provider>
  );
}
